import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { environment } from '../config/environment';
import { UserDetails } from '../services/user.types';
import { getBetaAppLogoutUrl } from '../utils/betaApp';

interface AuthContextType {
    currentUser: UserDetails | null;
    currentCtUser: UserDetails | null;
    logout: () => Promise<void>;
    isInitialized: boolean;
    isProcessingAuth: boolean;
    setIsProcessingAuth: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [currentUser, setCurrentUser] = useState<UserDetails | null>(null);
    const [isProcessingAuth, setIsProcessingAuth] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const queryClient = useQueryClient();

    // Fetch current user from CT cookies on mount
    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                console.log('[Auth] Fetching current user from CT cookies...');
                
                const response = await axios.get<{ success: boolean; data: UserDetails }>(
                    `${environment.expressApiUrl}/api/auth/me`,
                    { withCredentials: true }
                );

                if (response.data.success && response.data.data) {
                    console.log('[Auth] User authenticated:', response.data.data);
                    setCurrentUser(response.data.data);
                } else {
                    console.log('[Auth] No authenticated user');
                    setCurrentUser(null);
                }
            } catch (error) {
                console.log('[Auth] Not authenticated:', error);
                setCurrentUser(null);
            } finally {
                setIsInitialized(true);
            }
        };

        fetchCurrentUser();
    }, []);

    const logout = async () => {
        try {
            console.log('[Auth] Logging out...');
            
            // Call backend to clear cookies
            await axios.post(
                `${environment.expressApiUrl}/api/auth/logout`,
                {},
                { withCredentials: true }
            );
            
            // Clear local state
            setCurrentUser(null);
            
            // Clear React Query cache
            queryClient.clear();
            
            // Redirect to logout page
            // window.location.href = getBetaAppLogoutUrl();
            console.log('[Auth] Logout successful - would redirect to:', getBetaAppLogoutUrl());
        } catch (error) {
            console.error('[Auth] Error during logout:', error);
            // Clear state even if backend call fails
            setCurrentUser(null);
            queryClient.clear();
            // Redirect to logout page anyway
            // window.location.href = getBetaAppLogoutUrl();
            console.log('[Auth] Logout (after error) - would redirect to:', getBetaAppLogoutUrl());
        }
    };

    const value: AuthContextType = {
        currentUser,
        currentCtUser: currentUser, // Same as currentUser now
        logout,
        isInitialized,
        isProcessingAuth,
        setIsProcessingAuth,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
